import mapVehiclePerformance from './mapVehiclePerformance';
import mapVehicleTechnology from './mapVehicleTechnology';

const bodyStyleMap = {
	sedan: 'car',
	coupe: 'car',
	crossover: 'suv',
	'commercial-truck': 'truck'
};

export const inventoryTypeMap = {
	2: 'new',
	1: 'used',
	new: 'new',
	used: 'used',
	certified: 'cpo',
	all: 'all',
	default: 'all'
};

const getVehiclePrice = (vehicle) =>
	parseInt(vehicle.internetPrice, 10) > 0
		? vehicle.internetPrice
		: vehicle.msrp;

const getYear = (vehicle) => vehicle.modelYear || vehicle.year;

export default () => {
	const {
		confirmation = {
			vehicle: {}
		},
		page: {
			pageInfo: { isVdp, isVlp, pageId, pageName, params } = {},
			trackingTags = {}
		} = {},
		vehicles = []
	} = window?.DDC?.dataLayer || {};

	const urlSearchParams = new URLSearchParams(params);

	const pageAliasId = `${pageName},${pageId}`;
	const newVehiclePage = pageAliasId.includes('_NEW');
	const usedVehiclePage = pageAliasId.includes('_USED');
	const certifiedUsedVehiclePage = pageAliasId.includes('_CERTIFIED_USED');

	// this should be safe but if we run into an issue we can just put bodyStyle, make, model, year in here
	let templateStrings = {
		...trackingTags,
		...(trackingTags?.bodyStyle && { bodystyle: trackingTags.bodyStyle })
	};

	if (trackingTags?.make && trackingTags?.model) {
		templateStrings.nameplate = `${trackingTags.make} ${trackingTags.model}`;
	}

	let type = 'default';
	if (newVehiclePage) {
		type = 'new';
	} else if (certifiedUsedVehiclePage) {
		type = 'certified';
	} else if (usedVehiclePage) {
		type = 'used';
	} else if (urlSearchParams.get('attr_type')) {
		type = inventoryTypeMap[urlSearchParams.get('attr_type')];
	}

	templateStrings.inventoryType = inventoryTypeMap[type];

	const confirmationVehicle =
		confirmation?.vehicle || (confirmation?.uuid && confirmation) || {};

	if (vehicles.length || Object.keys(confirmationVehicle).length) {
		const vehicle = vehicles?.[0] || confirmationVehicle;

		if (Object.keys(vehicle).length) {
			templateStrings = {
				...vehicle,
				...templateStrings
			};
			templateStrings.bodystyle =
				bodyStyleMap[vehicle?.bodyStyle] ||
				vehicle?.bodyStyle?.toLowerCase();
			templateStrings.nameplate = `${vehicle.make} ${vehicle.model}`;

			if (Object.keys(confirmationVehicle).length) {
				const certified =
					confirmationVehicle?.certified === 'true' ||
					confirmationVehicle?.certified === true;
				templateStrings.inventoryType =
					inventoryTypeMap[
						certified ? 'certified' : confirmationVehicle.newOrUsed
					];
			}

			templateStrings.year = getYear(vehicle);
			templateStrings.vehiclePerformance = mapVehiclePerformance(
				vehicle.trim
			);
			templateStrings.vehiclePrice = getVehiclePrice(vehicle);

			templateStrings.vehicleTechnology = mapVehicleTechnology(
				vehicle.model
			);
		}

		/**
		 * If on page with multiple vehicles, only apply certain props
		 * if identical across all vehicles.
		 */
		if (vehicles.length > 1) {
			const isSingleMake = vehicles.every(
				(currentVehicle) => currentVehicle.make === vehicle.make
			);
			const isSingleModel = vehicles.every(
				(currentVehicle) => currentVehicle.model === vehicle.model
			);
			const isSingleBodyStyle = vehicles.every(
				(currentVehicle) =>
					currentVehicle.bodyStyle === vehicle.bodyStyle
			);
			const isSingleTrim = vehicles.every(
				(currentVehicle) => currentVehicle.trim === vehicle.trim
			);
			const isSingleExtColor = vehicles.every(
				(currentVehicle) =>
					currentVehicle.exteriorColor === vehicle.exteriorColor
			);
			const isSingleIntColor = vehicles.every(
				(currentVehicle) =>
					currentVehicle.interiorColor === vehicle.interiorColor
			);
			const isSingleEngine = vehicles.every(
				(currentVehicle) => currentVehicle.engine === vehicle.engine
			);
			const isSingleTransmission = vehicles.every(
				(currentVehicle) =>
					currentVehicle.transmission === vehicle.transmission
			);

			const isSingleYear = vehicles.every(
				(currentVehicle) => getYear(currentVehicle) === getYear(vehicle)
			);
			const isSinglePrice = vehicles.every(
				(currentVehicle) =>
					getVehiclePrice(currentVehicle) === getVehiclePrice(vehicle)
			);

			if (!isSingleMake || !isSingleModel) {
				delete templateStrings.nameplate;
			}

			if (!isSingleBodyStyle) {
				// Note uppercase S in the second line!
				delete templateStrings.bodystyle;
				delete templateStrings.bodyStyle;
			}
			if (!isSingleTrim) {
				delete templateStrings.trim;
				delete templateStrings.vehiclePerformance;
			}
			if (!isSingleExtColor) {
				delete templateStrings.exteriorColor;
			}
			if (!isSingleIntColor) {
				delete templateStrings.interiorColor;
			}
			if (!isSingleEngine) {
				delete templateStrings.engine;
			}
			if (!isSingleTransmission) {
				delete templateStrings.transmission;
			}
			if (!isSingleYear) {
				delete templateStrings.year;
			}
			if (!isSinglePrice) {
				delete templateStrings.vehiclePrice;
			}
		}
	} else if (
		urlSearchParams.get('bodyStyle') ||
		(urlSearchParams.get('make') && urlSearchParams.get('model'))
	) {
		// above looks goofy because urlSearchParams.size not working in unit tests
		// created this section for inventory finder confirm
		const bodyStyle = urlSearchParams.get('bodyStyle');
		if (bodyStyle) {
			templateStrings.bodystyle =
				bodyStyleMap[bodyStyle.toLowerCase()] || bodyStyle;
		}

		const make = urlSearchParams.get('make');
		const model = urlSearchParams.get('model');

		if (make && model) {
			templateStrings.nameplate = `${make} ${model}`;
		}
	}

	if (isVdp) {
		templateStrings.vehiclePageType = 'vehicle details';
	} else if (isVlp) {
		templateStrings.vehiclePageType = 'vls';
	}

	// set page title
	const pageTitle = document.querySelector('.ddc-page-title');

	if (pageTitle && pageTitle.textContent) {
		templateStrings.pageTitle = pageTitle.textContent.replace(/:/g, ' ');
	}

	return templateStrings;
};
